import React, { ChangeEvent, FC, Fragment, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { find, map, pull } from 'lodash';
import { CircularProgress, Checkbox, Chip, Grid, TextField, makeStyles } from '@material-ui/core';
import {
  Autocomplete,
  AutocompleteProps,
  RenderOptionState,
  GetTagProps,
  RenderInputParams,
} from '@material-ui/lab';
import AddCircleIcon from '@material-ui/icons/AddCircle';
import CheckBoxOutlineBlankIcon from '@material-ui/icons/CheckBoxOutlineBlank';
import CheckBoxIcon from '@material-ui/icons/CheckBox';
import CloseIcon from '@material-ui/icons/Close';

import { SegmentStatus, useGetUserSegmentsQuery, UserSegmentFragment } from 'api';
import { useNotify } from 'hooks';

type CustomProps<T> = Omit<AutocompleteProps<T>, 'renderInput' | 'options'>;
type SegmentIds = Array<UserSegmentFragment['id']>;
type Options = Array<Partial<UserSegmentFragment>>;

interface IProps extends CustomProps<UserSegmentFragment> {
  isCompanySelected: boolean;
  selectedSegmentsIds: SegmentIds;
  onSegmentChange: (segmentIds: SegmentIds) => void;
  onCompanySelected: () => void;
  validationError: string | string[];
}

const useStyles = makeStyles(theme => ({
  openIcon: {
    fontSize: theme.typography.pxToRem(32),
  },
  closeButton: {
    backgroundColor: theme.palette.secondary.main,
    fontSize: theme.typography.pxToRem(12),
    color: theme.palette.common.white,
    borderRadius: theme.spacing(3),
    padding: `${theme.spacing()}px ${theme.spacing(3)}px`,
  },
}));

const useAutoCompleteStyles = makeStyles(theme => ({
  hasPopupIcon: {},
  popupIndicator: {
    padding: 0,
  },
  popupIndicatorOpen: {
    transform: 'none',
  },
  endAdornment: {
    top: 'calc(50% - 16px)',
  },
  inputRoot: {
    '&[class*="MuiOutlinedInput-root"]': {
      '$hasPopupIcon &, $hasClearIcon &': {
        paddingRight: theme.spacing(13),
        minHeight: theme.spacing(7),
      },
      '& $endAdornment': {
        right: 20,
      },
    },
  },
  tag: {
    borderRadius: 4,
    fontWeight: theme.typography.fontWeightBold,
    backgroundColor: theme.palette.grey[100],
  },
  option: {
    paddingTop: 0,
    paddingBottom: 0,
    color: theme.palette.text.secondary,
    fontWeight: 500,
    '&[aria-selected="true"]': {
      color: theme.palette.text.primary,
      backgroundColor: 'transparent',
    },
    '&[data-focus="true"]': {
      color: theme.palette.text.primary,
      backgroundColor: 'transparent',
    },
    '&:active': {
      color: theme.palette.text.primary,
      backgroundColor: 'transparent',
    },
  },
}));

const COMPANY_ID = 'all_users';

const SegmentPicker: FC<IProps> = props => {
  const {
    isCompanySelected,
    selectedSegmentsIds,
    onSegmentChange,
    onCompanySelected,
    validationError,
    ...rest
  } = props;

  const [open, setOpen] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const { t } = useTranslation();

  const notify = useNotify();
  const classes = useStyles();
  const autocompleteClasses = useAutoCompleteStyles();

  const { loading, data } = useGetUserSegmentsQuery({
    onError: notify.mutationError,
    variables: { segment_status: SegmentStatus.Active },
  });

  const companySegment = {
    id: COMPANY_ID,
    title: t('components:entireCompany'),
  };

  const options = getOptions();
  const selectedOptions = getSelectedOptions();
  const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
  const checkedIcon = <CheckBoxIcon fontSize="small" />;

  function getOptions(): Options {
    if (!data || !data.getUserSegments) {
      return [];
    }

    return [companySegment, ...data.getUserSegments];
  }

  function getSelectedOptions() {
    if (isCompanySelected) {
      return [companySegment];
    }

    return selectedSegmentsIds.reduce((list, id) => {
      const segment = find(options, ['id', id]);
      if (!segment) {
        return list.concat([]);
      }
      return list.concat([segment]);
    }, []);
  }

  function toggleOpen() {
    setOpen(!open);
  }

  function handleChange(event: ChangeEvent, nextValue: Options) {
    const ids = map(nextValue, 'id');
    const isSelectingCompany = !isCompanySelected && ids.includes(COMPANY_ID);

    setInputValue('');

    if (isSelectingCompany) {
      return onCompanySelected();
    }

    onSegmentChange(pull(ids, COMPANY_ID));
  }

  function handleInputChange(event: ChangeEvent<HTMLInputElement>) {
    setInputValue(event.target.value);
  }

  function renderTags(value: Options, getTagProps: GetTagProps) {
    return value.map((segment, index) => (
      <Chip
        key={segment.id}
        label={segment.title}
        deleteIcon={<CloseIcon />}
        {...getTagProps({ index })}
      />
    ));
  }

  function renderPopupIcon() {
    return open ? (
      <div className={classes.closeButton}>{t('common:done')}</div>
    ) : (
      <AddCircleIcon className={classes.openIcon} color="secondary" />
    );
  }

  function renderOption(option: UserSegmentFragment, { selected }: RenderOptionState) {
    return (
      <Grid container alignItems="center">
        <Grid item xs>
          {option.title}
        </Grid>
        <Grid item>
          <Checkbox icon={icon} checkedIcon={checkedIcon} checked={selected} />
        </Grid>
      </Grid>
    );
  }

  function renderInput(params: RenderInputParams) {
    return (
      <TextField
        {...params}
        value={inputValue}
        variant="outlined"
        placeholder={t('components:segmentSuggest')}
        error={!!validationError}
        helperText={validationError}
        onChange={handleInputChange}
        InputProps={{
          ...params.InputProps,
          endAdornment: (
            <Fragment>
              {!open && loading && <CircularProgress color="inherit" size={24} />}
              {params.InputProps.endAdornment}
            </Fragment>
          ),
        }}
      />
    );
  }

  return (
    <Autocomplete
      multiple
      disableClearable
      disableCloseOnSelect
      open={open}
      onOpen={toggleOpen}
      onClose={toggleOpen}
      classes={autocompleteClasses}
      options={options}
      loading={loading}
      onChange={handleChange}
      getOptionLabel={({ title }) => title}
      popupIcon={renderPopupIcon()}
      renderTags={renderTags}
      renderOption={renderOption}
      renderInput={renderInput}
      value={selectedOptions}
      inputValue={inputValue}
      {...rest}
    />
  );
};

export default SegmentPicker;
